import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import clsx, { type ClassValue } from 'clsx'
import { RxDocument } from 'rxdb'
import { FlipCard } from 'ui/flip-card'

import { FadingFlipCard } from '~/boundedContext/cards/components/fading-flip-card'
import { SnoozeCountBadge } from '~/boundedContext/cards/components/snooze-count-badge'
import { getActiveSnoozeCount, getSnoozeDelayDays, isCardWasSnoozedForFront } from '~/boundedContext/cards/utils/card'
import { getSnoozeGameSurfaceClassName } from '~/boundedContext/cards/utils/snooze-palette'
import { Card } from '~/db/types'
import { useCardsGameStore } from '~/stores/cards-game'
import { useFadingCardStore } from '~/stores/fading-card'
import { useScoreStore } from '~/stores/score'
import { useSettingsStore } from '~/stores/settings'

import { useStackRefreshToast } from '../../hooks/use-stack-refresh-toast'
import { CardsMenuBar } from '../cards-menu-bar'
import { DraggableFlipCard } from '../draggable-flip-card'
import { Score } from '../score'

import { useCardActions } from './hooks'
import { getFrontBack } from './utils'

type ShuffledCardsProps = {
  activeCard: RxDocument<Card>
}

export const ShuffledCards = memo(({ activeCard }: ShuffledCardsProps) => {
  const shuffledCards = useCardsGameStore(state => state.shuffledCards)
  const activeItemIndex = useCardsGameStore(state => state.activeItemIndex)
  const reset = useCardsGameStore(state => state.reset)
  const startFadingCard = useFadingCardStore(state => state.startFadingCard)
  const notGuessedCards = useCardsGameStore(state => state.notGuessedCards)
  const cardsFront = useSettingsStore(state => state.cardsFront)
  const { t } = useTranslation()
  const isSnoozeActive = useScoreStore(state => state.isSnoozeActive)
  useStackRefreshToast()

  const isRevertLastCard = shuffledCards.length !== activeItemIndex + 1

  const getFlipCardSurfaceClassName = (card: RxDocument<Card>, className?: ClassValue) =>
    clsx(
      'border',
      className,
      isCardWasSnoozedForFront(card, cardsFront) && getSnoozeGameSurfaceClassName(card, cardsFront),
    )

  // TODO: decide what number of cards to render
  const sliceStart = Math.max(0, activeItemIndex - 4)
  const cardsBelowActive = shuffledCards.slice(sliceStart, activeItemIndex)

  const { cardUnknown, cardPlayed, revertLastCard, snoozeCard } = useCardActions(activeCard)

  const handleRefreshStack = () => {
    reset()
  }

  return (
    <div className="relative flex h-full flex-col justify-between gap-4 pl-[env(safe-area-inset-left)]">
      <div className="relative">
        <Score
          activeCardNumber={shuffledCards.length - activeItemIndex}
          cardsLength={shuffledCards.length}
          className="mt-3"
          guessedNumber={shuffledCards.length - activeItemIndex - notGuessedCards.length - 1}
          notGuessedNumber={notGuessedCards.length}
        />
        {isSnoozeActive && (
          <p className="pointer-events-none absolute left-1/2 top-full z-10 mt-1 -translate-x-1/2 text-center text-xs text-orange-700/90 dark:text-orange-300">
            {t('Snooze for {{count}} days', {
              count: getSnoozeDelayDays(getActiveSnoozeCount(activeCard, cardsFront) + 1),
            })}
          </p>
        )}
      </div>
      <div className="relative h-full max-h-[50vh] w-full sm-landscape:max-h-none sm-landscape:grow">
        {cardsBelowActive.map((card, idx) => (
          <div
            key={card.id}
            className={clsx(
              'absolute inset-x-0 top-0 h-full',
              (sliceStart + idx) % 2 ? '-translate-x-1 -translate-y-1' : 'translate-x-1 translate-y-1',
            )}
          >
            <FlipCard
              id={card.id}
              innerContainerClassName={getFlipCardSurfaceClassName(
                card,
                idx === cardsBelowActive.length - 1 && 'shadow-lg',
              )}
              {...getFrontBack(card, cardsFront)}
            >
              <SnoozeCountBadge card={card} className="pointer-events-none absolute right-2 top-2 z-10" />
            </FlipCard>
          </div>
        ))}

        <div
          className={clsx(
            'absolute inset-x-0 top-0 z-10 h-full',
            activeItemIndex % 2 ? '-translate-x-1 -translate-y-1' : 'translate-x-1 translate-y-1',
          )}
        >
          <DraggableFlipCard
            key={activeCard.id}
            examples={activeCard.examples}
            id={activeCard.id}
            innerContainerClassName={getFlipCardSurfaceClassName(activeCard, 'shadow-lg')}
            onCardSnoozed={snoozeCard}
            onCardUnknown={cardUnknown}
            onFadingStart={(deltaX, deltaY, rotate, scale) => {
              startFadingCard({
                id: activeCard.id,
                examples: activeCard.examples,
                pronunciation: activeCard.pronunciation,
                learnText: activeCard.learnText,
                sourceText: activeCard.sourceText,
                snoozeCountSource: activeCard.snoozeCountSource,
                snoozeCountLearn: activeCard.snoozeCountLearn,
                snoozedUntilSource: activeCard.snoozedUntilSource,
                snoozedUntilLearn: activeCard.snoozedUntilLearn,
                deltaX,
                deltaY,
                rotate,
                scale,
              })
              cardPlayed()
            }}
            pronunciation={activeCard.pronunciation}
            {...getFrontBack(activeCard, cardsFront)}
          >
            <SnoozeCountBadge card={activeCard} className="pointer-events-none absolute right-2 top-2 z-10" />
          </DraggableFlipCard>
        </div>
        <FadingFlipCard />
      </div>
      <CardsMenuBar
        activeCard={activeCard}
        isRevertLastCard={isRevertLastCard}
        onRefreshStack={handleRefreshStack}
        revertLastCard={revertLastCard}
      />
    </div>
  )
})

ShuffledCards.displayName = 'ShuffledCards'
